import { Link } from 'react-router-dom'
import {
  ServerAppSubscriptionStatusEnum,
  useFetchBusinessCurrentSubscription,
  useFetchCurrentBranchTimezone,
} from '@expane/data'
import { store } from 'store'
import { createCurrentDate, differenceInCalendarDays } from '@expane/date'
import { useTranslation } from 'react-i18next'
import { useDateFormatting } from '@expane/logic/useDateFormatting'
import { IoCloseCircleOutline, IoInformationCircleOutline, IoWarningOutline } from 'react-icons/io5'
import { IconType } from 'react-icons'

export const TRIAL_EXPIRING_STATUS = 'trialExpiring' as const

export type AppSubscriptionStatusType =
  | ServerAppSubscriptionStatusEnum
  | typeof TRIAL_EXPIRING_STATUS

const bannerConfigs: {
  match: AppSubscriptionStatusType
  text: string
  style: string
  Icon: IconType
}[] = [
  {
    match: ServerAppSubscriptionStatusEnum.Trialing,
    text: 'appSubscription.banner.trialing',
    style: 'bg-primary-100 text-primary-700 border border-primary-200',
    Icon: IoInformationCircleOutline,
  },
  {
    match: TRIAL_EXPIRING_STATUS,
    text: 'appSubscription.banner.trialExpiring',
    style: 'bg-amber-100 text-amber-700 border border-amber-200',
    Icon: IoWarningOutline,
  },
  {
    match: ServerAppSubscriptionStatusEnum.PastDue,
    text: 'appSubscription.banner.pastDue',
    style: 'bg-amber-100 text-amber-700 border border-amber-200',
    Icon: IoWarningOutline,
  },
  {
    match: ServerAppSubscriptionStatusEnum.Canceled,
    text: 'appSubscription.banner.canceled',
    style: 'bg-error-100 text-error-700 border border-error-200',
    Icon: IoCloseCircleOutline,
  },
]

export const BillingPlanBanner = () => {
  const branchId = store.branch.branchId
  const timezone = useFetchCurrentBranchTimezone(branchId)

  const { data: currentSubscription, isLoading } = useFetchBusinessCurrentSubscription(timezone)

  const formatDate = useDateFormatting(timezone)

  const { t } = useTranslation()

  // Если нет подписки — считаем статус Canceled
  let displayStatus: AppSubscriptionStatusType = ServerAppSubscriptionStatusEnum.Canceled
  let canceledAt: Date | null = null

  if (currentSubscription) {
    displayStatus =
      mapToAppSubscriptionStatus(
        currentSubscription.status,
        currentSubscription.canceledAt,
        timezone,
      ) ?? ServerAppSubscriptionStatusEnum.Canceled

    canceledAt = currentSubscription.canceledAt ?? null
  }

  // Не показываем баннер для статуса Active или если статус неизвестен
  if (!displayStatus || displayStatus === ServerAppSubscriptionStatusEnum.Active) return null

  const matched = bannerConfigs.find(config => config.match === displayStatus)
  if (!matched || isLoading) return null

  const { Icon } = matched

  return (
    <div
      className={`rounded-md font-bold items-center justify-center text-sm flex p-2 m-2 ${matched.style}`}
    >
      <Icon size="1.2rem" className="mr-1 shrink-0" />
      <p className="mr-1">
        {t(`${matched.text}.1`, {
          date: formatDate('historyDate', canceledAt),
        })}
      </p>
      <Link to="/settings/app-subscriptions" className="underline">
        {t(`${matched.text}.2`)}
      </Link>
    </div>
  )
}

const shouldMarkTrialAsExpiring = (
  status: ServerAppSubscriptionStatusEnum,
  canceledAt: Date | null,
  timezone: string | undefined,
) => {
  if (status !== ServerAppSubscriptionStatusEnum.Trialing || !canceledAt) return false

  const now = createCurrentDate(timezone)

  const daysLeft = differenceInCalendarDays(canceledAt, now)

  return daysLeft <= TRIAL_EXPIRING_THRESHOLD_DAYS
}

const mapToAppSubscriptionStatus = (
  status: ServerAppSubscriptionStatusEnum,
  canceledAt: Date | null,
  timezone: string | undefined,
): AppSubscriptionStatusType | undefined => {
  if (
    status === ServerAppSubscriptionStatusEnum.Trialing &&
    shouldMarkTrialAsExpiring(status, canceledAt, timezone)
  ) {
    return TRIAL_EXPIRING_STATUS
  }
  return status
}

const TRIAL_EXPIRING_THRESHOLD_DAYS = 3
